<script lang="ts">
import { defineComponent, ref, PropType, watch } from "vue";
import SingleOTPInput from "./SingleOTPInput.vue";
// conmponent heavily based on Vue3OtpInput
const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;
export default defineComponent({
  name: "OTPInput",
  components: {
    SingleOTPInput,
  },

  props: {
    modelValue: {
      type: String,
    },

    numInputs: {
      type: Number,
      default: 6,
    },

    conditionalClass: {
      type: Array as PropType<string[]>,
      default: () => [],
    },

    separator: {
      type: String,
      default: "-",
    },

    inputClasses: {
      type: [String, Array] as PropType<string[] | string>,
      default: "otp-input",
    },

    inputType: {
      type: String as PropType<"number" | "tel" | "letter-numeric" | "password">,
      validator: (value: string) => ["number", "tel", "letter-numeric", "password"].includes(value),
    },

    inputmode: {
      type: String,
      validator: (value: string) => ["numeric", "text", "tel", "none"].includes(value),
      default: "numeric",
    },

    shouldAutoFocus: {
      type: Boolean,
      default: false,
    },

    filterPasteStrings: {
      type: [String, Array] as PropType<string[] | string>,
      default: () => [],
    },

    placeholder: {
      type: Array as PropType<string[]>,
      default: () => [],
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    upperCase: {
      type: Boolean,
      default: false,
    },

    seperatorEveryN: {
      type: Number,
      default: 0,
    },

    clearInputTrigger: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "on-complete", "on-change"],
  setup(props, { emit }) {
    const activeInput = ref<number>(0);
    const otp = ref<string[]>([]);
    const oldOtp = ref<string[]>([]);

    watch(
      () => otp,
      () => {
        emit("update:modelValue", otp.value.join(""));
      },
      { deep: true }
    );

    watch(
      () => props.modelValue,
      () => {
        otp.value = props.modelValue?.split("") || [];
        if (props.modelValue === "") focusInput(0);
      }
    );

    const handleOnFocus = (index: number) => {
      activeInput.value = index;
    };
    const handleOnBlur = () => {
      activeInput.value = -1;
    };
    // Helper to return OTP from input
    const checkFilledAllInputs = () => {
      if (otp.value.join("").length === props.numInputs) {
        return emit("on-complete", otp.value.join(""));
      }
      return "Wait until the user enters the required number of characters";
    };
    // Focus on input by index
    const focusInput = (input: number) => {
      activeInput.value = Math.max(Math.min(props.numInputs - 1, input), 0);
    };
    // Focus on next input
    const focusNextInput = () => {
      focusInput(activeInput.value + 1);
    };
    // Focus on previous input
    const focusPrevInput = () => {
      focusInput(activeInput.value - 1);
    };
    // Change OTP value at focused input
    const changeCodeAtFocus = (value: number | string) => {
      oldOtp.value = Object.assign([], otp.value);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      otp.value[activeInput.value] = value;
      if (oldOtp.value.join("") !== otp.value.join("")) {
        emit("on-change", otp.value.join(""));
        checkFilledAllInputs();
      }
    };
    // Handle pasted OTP
    const handleOnPaste = (event: ClipboardEvent) => {
      event.preventDefault();
      if (!event.clipboardData) return;

      let pastedData = event.clipboardData.getData("text/plain");
      for (let filterSpring of props.filterPasteStrings) {
        pastedData = pastedData.replaceAll(filterSpring, "");
      }
      if (props.upperCase) {
        pastedData = pastedData.toUpperCase();
      }
      const pastedDataArray = pastedData.slice(0, props.numInputs - activeInput.value).split("");
      if (props.inputType === "number" && !pastedDataArray.join("").match(/^\d+$/)) {
        return "Invalid pasted data";
      }
      if (props.inputType === "letter-numeric" && !pastedDataArray.join("").match(/^\w+$/)) {
        return "Invalid pasted data";
      }

      // Paste data from focused input onwards
      const currentCharsInOtp = otp.value.slice(0, activeInput.value);
      const combinedWithPastedData = currentCharsInOtp.concat(pastedDataArray);
      combinedWithPastedData.slice(0, props.numInputs).forEach(function (value, i) {
        otp.value[i] = value;
      });
      focusInput(combinedWithPastedData.slice(0, props.numInputs).length);
      return checkFilledAllInputs();
    };
    const handleOnChange = (value: number) => {
      changeCodeAtFocus(value);
      focusNextInput();
    };
    const clearInput = () => {
      if (otp.value.length > 0) {
        emit("on-change", "");
      }
      otp.value = [];
      activeInput.value = 0;
    };
    // Handle cases of backspace, delete, left arrow, right arrow
    const handleOnKeyDown = (event: KeyboardEvent) => {
      switch (event.keyCode) {
        case BACKSPACE:
          event.preventDefault();
          changeCodeAtFocus("");
          focusPrevInput();
          break;
        case DELETE:
          event.preventDefault();
          changeCodeAtFocus("");
          break;
        case LEFT_ARROW:
          event.preventDefault();
          focusPrevInput();
          break;
        case RIGHT_ARROW:
          event.preventDefault();
          focusNextInput();
          break;
        default:
          break;
      }
    };
    return {
      activeInput,
      otp,
      oldOtp,
      clearInput,
      handleOnPaste,
      handleOnKeyDown,
      handleOnBlur,
      changeCodeAtFocus,
      focusInput,
      focusNextInput,
      focusPrevInput,
      handleOnFocus,
      checkFilledAllInputs,
      handleOnChange,
    };
  },
});
</script>

<template>
  <!--    To turn off autocomplete when otp-input is password-->
  <span class="otp-wrapper gap-1.5">
    <input v-if="inputType === 'password'" autocomplete="off" name="hidden" type="text" style="display: none" />

    <SingleOTPInput
      v-for="(_, i) in numInputs"
      :key="i"
      :focus="activeInput === i"
      :value="otp[i]"
      :separator="separator"
      :seperator-every-n="seperatorEveryN"
      :input-type="inputType"
      :inputmode="inputmode"
      :input-classes="inputClasses"
      :conditional-class="conditionalClass[i]"
      :is-last-child="i === numInputs - 1"
      :should-auto-focus="shouldAutoFocus"
      :upper-case="upperCase"
      placeholder=""
      :is-disabled="isDisabled"
      :input-number="i + 1"
      @onChange="handleOnChange"
      @onKeydown="handleOnKeyDown"
      @onPaste="handleOnPaste"
      @onFocus="handleOnFocus(i)"
      @onBlur="handleOnBlur"
    />
  </span>
</template>

<style scoped lang="scss">
.otp-wrapper {
  display: inline-flex;
  min-width: 10rem; //12.6rem;;
}
</style>
